import { formDraftSchema, sanitiseFormDraft, type FormDraft } from "@/features/ai-builder/formDraft";
import { toInputRow } from "@/features/dictionaries/attachDictionaryRows";
import { syncOwnedInputChoices } from "@/features/dictionaries/syncOwnedInputChoices";
import { supabaseClient } from "@/lib/supabase";
import type { Database } from "@patient-watch/types";
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { useAuth } from "@/utils/auth";
import {
    asLocalMutation,
    useFormBuilderWorkspace,
} from "@/features/forms/components/form-builder/formBuilderWorkspace";

type FormRow = Database["public"]["Tables"]["forms"]["Row"];
type FormUpdate = Database["public"]["Tables"]["forms"]["Update"];
type FormInsert = Database["public"]["Tables"]["forms"]["Insert"];
type PageRow = Database["public"]["Tables"]["pages"]["Row"];
type PageInsert = Database["public"]["Tables"]["pages"]["Insert"];
type InputRow = Database["public"]["Tables"]["inputs"]["Row"];
type InputInsert = Database["public"]["Tables"]["inputs"]["Insert"];
interface BlockRow {
    block_id: string;
    page_id: string;
    block_index: number;
    block_type: "text" | "image" | "video" | "image-carousel";
    content: string | null;
    media_url: string | null;
    caption: string | null;
    created_at?: string;
    last_edited?: string;
}
interface BlockInsert {
    block_id?: string;
    page_id: string;
    block_index?: number;
    block_type: "text" | "image" | "video" | "image-carousel";
    content?: string | null;
    media_url?: string | null;
    caption?: string | null;
}

export interface FormBuilderData extends FormRow {
    pages: (PageRow & { inputs: InputRow[]; blocks: BlockRow[] })[];
    /** Calculated scores with no page. Patients never see these. */
    hiddenInputs?: InputRow[];
}

export interface HiddenFieldDraft {
    input_label: string;
    input_index: number;
    min_value: number | null;
    max_value: number | null;
    max_score: number | null;
    chart_order: number | null;
    is_primary_score: boolean;
}

export function hiddenFieldsFromInputs(inputs: InputRow[] | undefined): HiddenFieldDraft[] {
    return (inputs ?? []).map((input) => ({
        input_label: input.input_label?.trim() || "Score",
        input_index: input.input_index,
        min_value: input.min_value,
        max_value: input.max_value,
        max_score: input.max_score,
        chart_order: input.chart_order,
        is_primary_score: input.is_primary_score,
    }));
}

async function selectFormForBuilder(formId: string): Promise<FormBuilderData> {
    const { data, error } = await supabaseClient
        .from("forms")
        .select("*, pages(*, inputs(*), blocks(*))")
        .eq("form_id", formId)
        .limit(1);

    if (error) throw error;
    const formRow = data?.[0];
    if (!formRow) throw new Error("Form not found");

    const { data: hiddenInputs, error: hiddenError } = await (supabaseClient.from("inputs") as any)
        .select("*")
        .eq("form_id", formId)
        .is("page_id", null)
        .order("input_index");
    if (hiddenError) throw hiddenError;

    const form = formRow as FormBuilderData;
    form.hiddenInputs = [...((hiddenInputs ?? []) as InputRow[])].sort((a, b) => a.input_index - b.input_index);
    form.pages = [...(form.pages || [])].sort((a, b) => a.page_index - b.page_index);
    for (const page of form.pages) {
        page.inputs = [...(page.inputs || [])].sort((a, b) => a.input_index - b.input_index);
        page.blocks = [...(page.blocks || [])].sort((a, b) => a.block_index - b.block_index);
    }

    return form;
}

export const formBuilderQueryOptions = (formId: string) =>
    queryOptions({
        queryKey: ["form-builder", formId],
        queryFn: () => selectFormForBuilder(formId),
    });

export function useFormBuilder(formId: string) {
    return useSuspenseQuery(formBuilderQueryOptions(formId));
}

export function useUpdateForm(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (updates: FormUpdate) => {
            const { data, error } = await supabaseClient
                .from("forms")
                .update(updates)
                .eq("form_id", formId)
                .select()
                .single();
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            qc.invalidateQueries({ queryKey: ["forms"] });
            qc.invalidateQueries({ queryKey: ["form"] });
        },
        onError: () => toast.error(toastCopy.failedUpdateForm),
    });
    if (workspace) {
        return asLocalMutation((updates: FormUpdate) => {
            workspace.setForm((form) => ({ ...form, ...updates }));
            return { ...workspace.form, ...updates };
        });
    }
    return mutation;
}

export function useUpsertPage(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (page: PageInsert) => {
            if (page.page_id) {
                const updates = Object.fromEntries(
                    Object.entries(page).filter(([key, value]) => key !== "page_id" && value !== undefined)
                );
                const { data, error } = await supabaseClient
                    .from("pages")
                    .update(updates)
                    .eq("page_id", page.page_id)
                    .select()
                    .maybeSingle();
                if (error) throw error;
                // Newly created blocks (Add Block, duplicate) match no row yet - fall through to insert.
                if (data) return data;
            }
            const { data, error } = await supabaseClient.from("pages").upsert(page).select().single();
            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            if (data) {
                qc.setQueryData<FormBuilderData>(["form-builder", formId], (current) => {
                    if (!current) return current;
                    const exists = current.pages.some((page) => page.page_id === data.page_id);
                    const pages = exists
                        ? current.pages.map((page) =>
                              page.page_id === data.page_id
                                  ? { ...page, ...data, inputs: page.inputs, blocks: page.blocks }
                                  : page
                          )
                        : [...current.pages, { ...data, inputs: [], blocks: [] }];
                    return {
                        ...current,
                        pages: [...pages].sort((a, b) => a.page_index - b.page_index),
                    };
                });
            }
            void qc.invalidateQueries({ queryKey: ["form-builder", formId] });
        },
        onError: () => toast.error(toastCopy.failedSavePage),
    });
    if (workspace) {
        return asLocalMutation((page: PageInsert) => {
            workspace.setForm((form) => {
                const pageId = page.page_id ?? crypto.randomUUID();
                const existing = form.pages.find((item) => item.page_id === pageId);
                const nextPage = {
                    ...(existing ?? {
                        page_id: pageId,
                        form_id: form.form_id,
                        page_index: page.page_index ?? form.pages.length + 1,
                        page_label: page.page_label ?? `Block ${form.pages.length + 1}`,
                        page_description: null,
                        page_logic: null,
                        inputs: [],
                        blocks: [],
                    }),
                    ...page,
                    page_id: pageId,
                    form_id: form.form_id,
                };
                if (existing) {
                    return {
                        ...form,
                        pages: form.pages.map((item) => (item.page_id === pageId ? { ...item, ...nextPage } : item)),
                    };
                }
                return { ...form, pages: [...form.pages, nextPage] };
            });
            return page;
        });
    }
    return mutation;
}

export function useDeletePage(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (pageId: string) => {
            const { error } = await supabaseClient.from("pages").delete().eq("page_id", pageId);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            toast.success(toastCopy.pageDeleted);
        },
        onError: () => toast.error(toastCopy.failedDeletePage),
    });
    if (workspace) {
        return asLocalMutation((pageId: string) => {
            workspace.setForm((form) => ({ ...form, pages: form.pages.filter((page) => page.page_id !== pageId) }));
        });
    }
    return mutation;
}

export function useUpsertBuilderInput(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (input: InputInsert) => {
            const payload =
                input.page_id == null
                    ? { ...toInputRow(input), page_id: null, form_id: formId, input_key: "number" }
                    : toInputRow(input);
            const { data, error } = await (supabaseClient.from("inputs") as any).upsert(payload).select().single();
            if (error) throw error;
            const row = data as InputRow;
            await syncOwnedInputChoices(row);
            return row;
        },
        onSuccess: (data) => {
            qc.setQueryData<FormBuilderData>(["form-builder", formId], (current) => {
                if (!current) return current;
                if (data.page_id == null) {
                    const hidden = current.hiddenInputs ?? [];
                    const exists = hidden.some((input) => input.input_id === data.input_id);
                    const next = exists
                        ? hidden.map((input) => (input.input_id === data.input_id ? data : input))
                        : [...hidden, data];
                    return {
                        ...current,
                        hiddenInputs: [...next].sort((a, b) => a.input_index - b.input_index),
                    };
                }
                return {
                    ...current,
                    pages: current.pages.map((page) => {
                        if (page.page_id !== data.page_id) return page;
                        const exists = page.inputs.some((input) => input.input_id === data.input_id);
                        const inputs = exists
                            ? page.inputs.map((input) => (input.input_id === data.input_id ? data : input))
                            : [...page.inputs, data];
                        return {
                            ...page,
                            inputs: [...inputs].sort((a, b) => a.input_index - b.input_index),
                        };
                    }),
                };
            });
            void qc.invalidateQueries({ queryKey: ["form-builder", formId] });
        },
        onError: () => toast.error(toastCopy.failedSaveQuestion),
    });
    if (workspace) {
        return asLocalMutation((input: InputInsert) => {
            workspace.setForm((form) => {
                if (input.page_id == null) {
                    const hidden = form.hiddenInputs ?? [];
                    const inputId = input.input_id ?? crypto.randomUUID();
                    const existing = hidden.find((item) => item.input_id === inputId);
                    const nextInput = {
                        ...(existing ?? { input_id: inputId, page_id: null, input_key: "number" as const }),
                        ...input,
                        input_id: inputId,
                        page_id: null,
                        input_key: "number" as const,
                    };
                    const next = existing
                        ? hidden.map((item) => (item.input_id === inputId ? { ...item, ...nextInput } : item))
                        : [...hidden, nextInput];
                    return { ...form, hiddenInputs: next };
                }
                return {
                    ...form,
                    pages: form.pages.map((page) => {
                        if (page.page_id !== input.page_id) return page;
                        const inputId = input.input_id ?? crypto.randomUUID();
                        const existing = page.inputs.find((item) => item.input_id === inputId);
                        const nextInput = {
                            ...(existing ?? { input_id: inputId, page_id: page.page_id }),
                            ...input,
                            input_id: inputId,
                        };
                        if (existing) {
                            return {
                                ...page,
                                inputs: page.inputs.map((item) =>
                                    item.input_id === inputId ? { ...item, ...nextInput } : item
                                ),
                            };
                        }
                        return { ...page, inputs: [...page.inputs, nextInput] };
                    }),
                };
            });
            return input;
        });
    }
    return mutation;
}

export function useDeleteBuilderInput(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (inputId: string) => {
            const { error } = await supabaseClient.from("inputs").delete().eq("input_id", inputId);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            toast.success(toastCopy.questionDeleted);
        },
        onError: () => toast.error(toastCopy.failedDeleteQuestion),
    });
    if (workspace) {
        return asLocalMutation((inputId: string) => {
            workspace.setForm((form) => ({
                ...form,
                hiddenInputs: (form.hiddenInputs ?? []).filter((input) => input.input_id !== inputId),
                pages: form.pages.map((page) => ({
                    ...page,
                    inputs: page.inputs.filter((input) => input.input_id !== inputId),
                })),
            }));
        });
    }
    return mutation;
}

export type StandardFormCalculator = "none" | "total";

export async function createFormFromDraft(
    draft: FormDraft,
    calculator: StandardFormCalculator = "none",
    hiddenFields: HiddenFieldDraft[] = []
) {
    const sanitised = sanitiseFormDraft(draft);
    const parsed = sanitised.draft ? formDraftSchema.safeParse(sanitised.draft) : null;
    if (!parsed?.success) throw new Error("The draft is not valid.");
    if (calculator !== "none" && calculator !== "total") {
        throw new Error("Choose None or Total.");
    }

    const { data, error } = await (supabaseClient as any).rpc("create_form_from_ai_draft", {
        p_draft: { ...parsed.data, calculator, hidden_fields: hiddenFields },
    });
    if (error) throw error;
    if (typeof data !== "string") throw new Error("The form draft could not be created.");
    return { formId: data };
}

export interface CreateFormFromDraftInput {
    draft: FormDraft;
    calculator?: StandardFormCalculator;
    hiddenFields?: HiddenFieldDraft[];
}

export function useCreateFormFromDraft() {
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ draft, calculator, hiddenFields }: CreateFormFromDraftInput) =>
            createFormFromDraft(draft, calculator ?? "none", hiddenFields ?? []),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["forms"] });
            toast.success(toastCopy.formDraftCreated);
        },
        onError: () => toast.error(toastCopy.failedCreateFormFromDraft),
    });
}

export async function applyAiFormDraft(formId: string, draft: FormDraft) {
    const sanitised = sanitiseFormDraft(draft);
    const parsed = sanitised.draft ? formDraftSchema.safeParse(sanitised.draft) : null;
    if (!parsed?.success) throw new Error("The draft is not valid.");

    const { data, error } = await (supabaseClient as any).rpc("apply_ai_form_draft", {
        p_form_id: formId,
        p_draft: parsed.data,
    });
    if (error) throw error;
    if (data !== formId) throw new Error("The form changes could not be applied.");
    return { formId: data };
}

export function useApplyAiFormDraft(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationFn: (draft: FormDraft) => applyAiFormDraft(formId, draft),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            qc.invalidateQueries({ queryKey: ["forms"] });
            toast.success(toastCopy.formUpdated);
        },
        onError: () => toast.error(toastCopy.failedUpdateForm),
    });
    if (workspace) {
        return asLocalMutation((draft: FormDraft) => {
            workspace.applyDraft(draft);
            return { formId };
        });
    }
    return mutation;
}

export async function setFormPublished(formId: string, published: boolean) {
    const { data, error } = await (supabaseClient as any).rpc("set_form_published", {
        p_form_id: formId,
        p_published: published,
    });
    if (error) throw error;
    if (data !== published) throw new Error("The publication state could not be changed.");
    return data;
}

export function useSetFormPublished(formId: string) {
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (published: boolean) => setFormPublished(formId, published),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            qc.invalidateQueries({ queryKey: ["forms"] });
            qc.invalidateQueries({ queryKey: ["form"] });
            toast.success(toastCopy.formUpdated);
        },
        onError: () => toast.error(toastCopy.failedUpdateForm),
    });
}

export async function setStandardFormCalculator(formId: string, calculator: StandardFormCalculator) {
    const { data, error } = await (supabaseClient as any).rpc("set_form_standard_calculator", {
        p_form_id: formId,
        p_calculator: calculator,
    });
    if (error) throw error;
    if (data !== calculator) throw new Error("The calculator could not be changed.");
    return data as StandardFormCalculator;
}

export function useSetStandardFormCalculator(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationFn: (calculator: StandardFormCalculator) => setStandardFormCalculator(formId, calculator),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            qc.invalidateQueries({ queryKey: ["forms"] });
            toast.success(toastCopy.formUpdated);
        },
        onError: () => toast.error(toastCopy.failedUpdateForm),
    });
    if (workspace) {
        return asLocalMutation((calculator: StandardFormCalculator) => {
            workspace.setForm((form) => {
                if (calculator === "none") {
                    return { ...form, score_engine: null, score_engine_config: null };
                }
                const outputId = crypto.randomUUID();
                return {
                    ...form,
                    score_engine: "sum_total",
                    score_engine_config: {
                        output_input_id: outputId,
                        source_input_keys: ["scale-rating", "number", "slider"],
                        managed_by: "form-builder",
                    },
                };
            });
            return calculator;
        });
    }
    return mutation;
}

export function useCreateForm() {
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const auth = useAuth();
    return useMutation({
        mutationFn: async () => {
            const formId = crypto.randomUUID();
            const pageId = crypto.randomUUID();

            const formInsert: FormInsert = {
                form_id: formId,
                form_name: "Untitled form",
                form_description: "",
                status: "draft",
                is_published: false,
                show_chart: false,
                is_side_specific: false,
                author: auth.id,
            };

            const { error: formError } = await supabaseClient.from("forms").insert(formInsert);
            if (formError) throw formError;

            const pageInsert: PageInsert = {
                page_id: pageId,
                form_id: formId,
                page_label: null,
                page_index: 1,
            };

            const { error: pageError } = await supabaseClient.from("pages").insert(pageInsert);
            if (pageError) throw pageError;

            return { formId };
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["forms"] });
            toast.success(toastCopy.formUpdated);
        },
        onError: () => toast.error(toastCopy.failedUpdateForm),
    });
}

export function useUpsertBuilderBlock(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (block: BlockInsert) => {
            const { data, error } = await (supabaseClient as any).from("blocks").upsert(block).select().single();
            if (error) throw error;
            return data;
        },
        onSuccess: (data: BlockRow) => {
            qc.setQueryData<FormBuilderData>(["form-builder", formId], (current) => {
                if (!current) return current;
                return {
                    ...current,
                    pages: current.pages.map((page) => {
                        if (page.page_id !== data.page_id) return page;
                        const exists = page.blocks.some((block) => block.block_id === data.block_id);
                        const blocks = exists
                            ? page.blocks.map((block) => (block.block_id === data.block_id ? data : block))
                            : [...page.blocks, data];
                        return {
                            ...page,
                            blocks: [...blocks].sort((a, b) => a.block_index - b.block_index),
                        };
                    }),
                };
            });
            void qc.invalidateQueries({ queryKey: ["form-builder", formId] });
        },
        onError: () => toast.error(toastCopy.failedSaveQuestion),
    });
    if (workspace) {
        return asLocalMutation((block: BlockInsert) => {
            workspace.setForm((form) => ({
                ...form,
                pages: form.pages.map((page) => {
                    if (page.page_id !== block.page_id) return page;
                    const blockId = block.block_id ?? crypto.randomUUID();
                    const existing = page.blocks.find((item) => item.block_id === blockId);
                    const nextBlock = {
                        block_id: blockId,
                        page_id: page.page_id,
                        block_index: block.block_index ?? page.blocks.length + 1,
                        block_type: block.block_type,
                        content: block.content ?? null,
                        media_url: block.media_url ?? null,
                        caption: block.caption ?? null,
                    };
                    if (existing) {
                        return {
                            ...page,
                            blocks: page.blocks.map((item) =>
                                item.block_id === blockId ? { ...item, ...nextBlock } : item
                            ),
                        };
                    }
                    return { ...page, blocks: [...page.blocks, nextBlock] };
                }),
            }));
            return block;
        });
    }
    return mutation;
}

export function useDeleteBuilderBlock(formId: string) {
    const workspace = useFormBuilderWorkspace();
    const toastCopy = useToastCopy();
    const qc = useQueryClient();
    const mutation = useMutation({
        mutationKey: ["form-builder-save", formId],
        mutationFn: async (blockId: string) => {
            const { error } = await (supabaseClient as any).from("blocks").delete().eq("block_id", blockId);
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["form-builder", formId] });
            toast.success(toastCopy.questionDeleted);
        },
        onError: () => toast.error(toastCopy.failedDeleteQuestion),
    });
    if (workspace) {
        return asLocalMutation((blockId: string) => {
            workspace.setForm((form) => ({
                ...form,
                pages: form.pages.map((page) => ({
                    ...page,
                    blocks: page.blocks.filter((block) => block.block_id !== blockId),
                })),
            }));
        });
    }
    return mutation;
}
